import { useState } from 'react';
import { Pencil, Ban } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import type { Cartao } from '../types';

const TIPO_LABEL: Record<number, string> = { 0: 'Ambos', 1: 'Débito', 2: 'Crédito' };

interface Props {
  cartoes: Cartao[];
  onEdit: (cartao: Cartao) => void;
  onInativar: (id: number) => void;
}

export const CartoesTable = ({ cartoes, onEdit, onInativar }: Props) => {
  const [inativarId, setInativarId] = useState<number | null>(null);

  return (
    <div className="space-y-4">
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nome</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Identificação</TableHead>
              <TableHead className="text-center">Voucher</TableHead>
              <TableHead className="text-center">Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cartoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Nenhum cartão cadastrado
                </TableCell>
              </TableRow>
            ) : (
              cartoes.map((cartao) => (
                <TableRow key={cartao.id}>
                  <TableCell className="font-medium">{cartao.nome}</TableCell>
                  <TableCell>{TIPO_LABEL[cartao.tipoDebitoCredito] ?? '-'}</TableCell>
                  <TableCell className="font-mono">{cartao.numeroIdentificacao || '-'}</TableCell>
                  <TableCell className="text-center">
                    {cartao.isVoucher ? <Badge variant="outline">Voucher</Badge> : '-'}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={cartao.isAtiva ? 'success' : 'secondary'}>
                      {cartao.isAtiva ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(cartao)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {cartao.isAtiva && (
                        <Button variant="ghost" size="icon" onClick={() => setInativarId(cartao.id)}>
                          <Ban className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!inativarId} onOpenChange={() => setInativarId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Inativar cartão</AlertDialogTitle>
            <AlertDialogDescription>Tem certeza que deseja inativar este cartão?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (inativarId) { onInativar(inativarId); setInativarId(null); } }}>
              Inativar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
